import EventDispatcher from './EventDispatcher';
import logger from './logging';

const log = logger('domain');

export default abstract class DomainModel extends EventDispatcher {

    /**
     * The fields map will contain a value for every field defined on the
     * class, including null or undefined values. Thus the keys may be used as
     * as list of fields on the class at runtime. (NB: for the map class, after
     * running `map.set('p', undefined)`, the query `map.has('p')` will be
     * true.
     */
    protected fields: Map<string, unknown>;

    /**
     * This is a sparse map of field names and values that temporarily hide the
     * values in the fields map. The values remain temporary until a commit
     * call copies them into the fields map.
     */
    protected temporary: Map<string, unknown> = new Map();

    public parent?: DomainModel;

    static fromJSON(_data: unknown, _parent?: DomainModel): DomainModel {
        return null;
    }

    /**
     * @param parent The domain model that owns this instance either directly
     * as a property, or indirectly in an array. This is set automatically by 
     * fromJSON().
     */
    constructor(parent?: DomainModel)
    {
        super();
        this.parent = parent;
        this.fields = new Map();
    }

    /**
     * Gets the current value of a field. The value returned will be whatever
     * temporary value is currently in use or the last committed value if there
     * has been no change since the last commit.
     */
    get(field: string): unknown {
        if(this.temporary.has(field)) {
            return this.temporary.get(field);
        } else {
            const value = this.fields.get(field);
            if(Array.isArray(value)) {
                // If we are dealing with an array field, then we want to
                // return a proxy that will route updates to the temporary.
                // The temporary is lazily initialized from a clone of the
                // original.
                const proxy = new Proxy(value, {
                    set: (_target, property, value) => {
                        let temp = this.temporary.get(field);
                        if(! temp) {
                            temp = Array.from(
                                this.fields.get(field) as Iterable<unknown>);
                            this.temporary.set(field, temp);
                        }
                        temp[property] = value;
                        return true;
                    },

                    get: (target, property) => {
                        let temp = this.temporary.get(field);
                        if(temp) {
                            return temp[property];
                        } else {
                            return target[property];
                        }
                    }
                });
                return proxy;
            } else {
                return value;
            }
        }
    }

    /**
     * Gets the last committed value of a field, bypassing the temporary
     * fields.
     */
    original(field: string): unknown{
        return this.fields.get(field);
    }

    /**
     * Sets the value of the given field. The get method will then return the
     * given value. The new value can be reverted by calling the reset method,
     * or it can be made "permanent" by calling the commit method. If the given
     * value is different to the last set value, then an event is triggered for
     * the field name.
     */
    set(field: string, value: unknown) {
        if(this.fields.has(field)) {
            let oldValue = this.get(field);
            if(value !== oldValue) {
                this.temporary.set(field, value);
                this.trigger(field);
                this.trigger('change', field);
            }
        }
        else {
            log.error(`Attempt to set non-existent field named "${field}"`);
        }
    }

    /**
     * Commit the temporary values. This is like committing a database
     * transaction in the sense that any values you set on the domain model
     * appear to be changed, but can be rolled back by calling reset. Once you
     * call commit, the new values are now the baseline for the next reset.
     */
    commit(recursive: boolean = false) {
        for(let [field, value] of this.temporary) {
            this.fields.set(field, value);
        }
        this.temporary.clear();

        if(recursive) {
            for(let value of this.fields.values()) {
                if(value instanceof DomainModel) {
                    value.commit(true);
                } else if(Array.isArray(value)) {
                    for(let item of value) {
                        if(item instanceof DomainModel) {
                            item.commit(true);
                        }
                    }
                }
            }
        }

        this.trigger('commit');
    }

    /**
     * Clear out any temporary values and revert to the last committed set of
     * field values.
     */
    reset() {
        // Resetting should trigger all the change events as these take on a
        // new value.
        this.batch(() => {
            for(let field of this.temporary.keys()) {
                this.set(field, this.fields.get(field));
            }
            this.temporary.clear();
        });
        this.trigger('reset');
    }

    /**
     * This converts the field values to a plain old JavaScript object so that
     * the JSON.stringify() method can work on domain models. Note that any
     * temporary values that have not been committed will be ignored.
     */
    toJSON() {
        var result = new Object();

        for(let [key, value] of this.fields) {
            if( value != null
                && typeof(value) == 'object'
                && ('toJSON' in value))
            {
                value = (value as any).toJSON();
            } else if( value != null && Array.isArray(value) ) {
                // Note that this will not support multi-dimensional arrays; if
                // we need to support that, this method can be implemented as
                // a call to a recursive function instead
                value = value.map(item => {
                    if(typeof item == 'object' && 'toJSON' in item) {
                        return (item as any).toJSON();
                    } else {
                        return item;
                    }
                });
            }
            result[key] = value;
        }
        return result;
    }

    /**
     * Recursively dump all event handlers bound to this object. This happens
     * during app.refresh() so that old views don't continue to receive events
     * from the model.
     */
    unbindAll() {
        this.alloff();

        const allValues = [
            ...this.fields.values(),
            ...this.temporary.values()
        ];
        for(let value of allValues) {
            if(value instanceof DomainModel) {
                value.unbindAll();
            } else if(Array.isArray(value)) {
                for(let item of value) {
                    if(item instanceof DomainModel) {
                        item.unbindAll();
                    }
                }
            }
        }
    }

    /**
     * Similar to the traverse function of the Object3D class from three.js,
     * this will visit the current domain model and all of its children and
     * call the given function on every domain model instance. Each model is
     * visited first, then each of its children.
     */
    traverse(fn: (model: DomainModel) => void) {
        try {
            fn(this);
        } catch(error) {
            log.error('DomainModel.traverse', error);
        }
        for(const field of this.fields.values()) {
            if(field == null) {
                continue;
            }
            else if(field instanceof DomainModel) {
                field.traverse(fn);
            }
            else if(Array.isArray(field)) {
                if(field.length > 0 && field[0] instanceof DomainModel) {
                    for(const item of field) {
                        item.traverse(fn);
                    }
                }
            }
        }
    }

    /**
     * A generator version of the member's traverse() function. This doesn't
     * use a callback so it can be exited early.
     */
    static *traverse(model: DomainModel) {
        if(!model) return;
        yield model;
        for(const field of model.fields.values()) {
            if(field == null) {
                continue;
            }
            else if(field instanceof DomainModel) {
                yield* DomainModel.traverse(field);
            }
            else if(Array.isArray(field)) {
                if(field.length > 0 && field[0] instanceof DomainModel) {
                    for(const item of field) {
                        yield* DomainModel.traverse(item);
                    }
                }
            }
        }

    }
}



